/* The buy block.

   The option pickers are the variant picker: one per product option, drawn as
   swatches for the colourway option and pills for the rest. A value does not
   identify a variant on its own once there is more than one option, so the
   selection is read from every picker and the variant resolved from the
   combination. Clicking one selects the variant and
   updates everything that depends on it, then broadcasts hystride:variant so
   the sections that live outside this one — Know it fits, Worn by you, the
   sticky buy bar — can follow. That broadcast is the whole reason those
   sections work at all; Horizon's own event only reaches its own section.

   The gallery is rendered once for every variant and only toggled. The
   prototype rebuilds it on each colour change, refetching seven 1400px images
   every time — 56 large downloads across eight colours, plus a reset of the
   thumbnail scroll and any focus inside it. */
(function () {
  'use strict';

  const H = window.Hystride;
  if (!H) return;

  H.register('main-product', {
    mount(root) {
      const stage = root.querySelector('[data-pdp-stage]');
      const thumbs = root.querySelector('[data-pdp-thumbs]');
      const input = root.querySelector('[data-pdp-variant-input]');
      const submit = root.querySelector('[data-pdp-submit]');

      const colourEl = root.querySelector('[data-pdp-colour]');
      const priceEl = root.querySelector('[data-pdp-price]');
      const btnPriceEl = root.querySelector('[data-pdp-button-price]');
      const noteEl = root.querySelector('[data-pdp-note]');
      const condEl = root.querySelector('[data-pdp-cond]');
      const stockEl = root.querySelector('[data-pdp-stock]');

      /* Which media belongs to the selected colour.

         The liquid template does the sourcing (variant.metafields.custom.gallery
         when a colourway has one, product.media owner-matching otherwise,
         docs/02 §2) and renders every candidate image for every variant up
         front, each tagged data-variant. This script only ever shows/hides —
         it never re-fetches, so switching colour costs nothing over the wire
         even for a product with dozens of media items across colourways. */
      const limit = parseInt(root.dataset.mediaLimit, 10) || 7;

      /* Auglio's virtual try-on script inserts its own <button
         class="auglio-tryon-btn"> as the first child of [data-pdp-stage] —
         that element doubles as its required ".product-gallery__media-list-
         wrapper" container (see the comment in hystride-main-product.liquid).
         Every index below assumes container.children is only ever our own
         gallery images/thumbs, so that button has to be filtered out here or
         it shifts every real image's position by one — verified live: it's
         exactly what made a thumbnail click light up the try-on button
         instead of an image. */
      const galleryChildren = (container) =>
        Array.from(container.children).filter((el) => !el.classList.contains('auglio-tryon-btn'));

      const showFor = (container, variantId) => {
        if (!container) return [];
        const kids = galleryChildren(container);

        const mine = kids.filter((el) => el.dataset.variant === variantId);
        const shared = kids.filter((el) => !el.dataset.variant);

        /* A curated gallery (custom.gallery on the variant) is an explicit
           choice about which photos this colour gets, so it stands alone:
           every one of them, in order, and nothing padded in behind them.
           Only the product.media fallback mixes in the media that belongs to
           no variant, and only that path is capped by "Photos shown per
           colour" — otherwise a ninth curated photo would be rendered by
           Liquid and then dropped here. */
        const curated = mine.some((el) => el.dataset.curated === 'true');
        const visible = curated ? mine : mine.concat(shared).slice(0, limit);

        kids.forEach((el) => {
          el.hidden = !visible.includes(el);
          el.classList.remove('on');
        });
        // Re-order so the variant's own photo leads, whatever the media order.
        visible.forEach((el) => container.appendChild(el));
        if (visible[0]) visible[0].classList.add('on');
        return visible;
      };

      /* Every variant's option values and state, published by Liquid. A
         value no longer identifies a variant on its own: with two options it
         takes the whole combination, so selection is tracked per option and
         the variant is looked up from all of them together. */
      let variants = [];
      try {
        variants = JSON.parse(root.querySelector('[data-pdp-variants]').textContent);
      } catch {
        variants = [];
      }

      const groups = Array.from(root.querySelectorAll('[data-pdp-option]'));
      const buttonsIn = (group) => Array.from(group.querySelectorAll('button[data-option-position]'));
      const allButtons = groups.flatMap(buttonsIn);

      const currentSelection = () =>
        groups.map((group) => {
          const on = buttonsIn(group).find((b) => b.getAttribute('aria-current') === 'true');
          return on ? on.dataset.value : null;
        });

      const findVariant = (selection) =>
        variants.find((variant) =>
          selection.every((value, i) => value === null || variant.options[i] === value)
        );

      /* Which values are reachable given what is chosen in the other
         pickers. A combination that does not exist at all is worth saying so
         before the shopper clicks it, rather than after. */
      const markUnavailable = (selection) => {
        groups.forEach((group, gi) => {
          buttonsIn(group).forEach((btn) => {
            const probe = selection.slice();
            probe[gi] = btn.dataset.value;
            const match = findVariant(probe);
            btn.classList.toggle('is-unavailable', !match || !match.available);
          });
        });
      };

      const apply = (variant, selection) => {
        if (!variant) return;

        if (input) input.value = variant.id;

        groups.forEach((group, gi) => {
          const valueEl = group.querySelector('[data-pdp-option-value]');
          if (valueEl) valueEl.textContent = selection[gi] || '';
          // The --lens accent follows the swatch picker's own chosen colour.
          const on = buttonsIn(group).find((b) => b.getAttribute('aria-current') === 'true');
          const head = group.querySelector('.opt-h');
          if (on && head) {
            const c = getComputedStyle(on).getPropertyValue('--c').trim();
            if (c) head.style.setProperty('--lens', c);
          }
        });

        if (colourEl) colourEl.textContent = selection.filter(Boolean).join(' / ');
        if (priceEl) priceEl.textContent = variant.price;
        if (btnPriceEl) btnPriceEl.textContent = variant.price;
        if (noteEl) noteEl.textContent = variant.note || '';

        if (condEl) {
          condEl.innerHTML = '';
          if (variant.cond) {
            const tag = document.createElement('span');
            tag.textContent = variant.cond;
            condEl.appendChild(tag);
          }
          if (variant.use) condEl.appendChild(document.createTextNode(variant.use));
        }

        if (submit) submit.disabled = !variant.available;
        if (stockEl) stockEl.dataset.available = String(variant.available);

        showFor(stage, String(variant.id));
        showFor(thumbs, String(variant.id));
        markUnavailable(selection);

        // Keep the address bar in step so the page is shareable and refresh
        // lands on the same variant.
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Tell the rest of the page. hystride-variant-bridge.js re-broadcasts
        // Horizon's own event the same way, so listeners only handle one.
        document.dispatchEvent(
          new CustomEvent('hystride:variant', {
            detail: {
              id: Number(variant.id),
              title: selection.filter(Boolean).join(' / '),
              price: variant.price,
              available: variant.available,
            },
          })
        );
      };

      const select = (btn) => {
        const group = btn.closest('[data-pdp-option]');
        if (!group) return;

        buttonsIn(group).forEach((b) =>
          b.setAttribute('aria-current', b === btn ? 'true' : 'false')
        );

        const selection = currentSelection();
        apply(findVariant(selection), selection);
      };

      const onSwatch = (e) => select(e.currentTarget);
      allButtons.forEach((b) => b.addEventListener('click', onSwatch));

      // Apply the same rule to the first paint. Liquid renders every media item
      // so the gallery survives with JS disabled; this trims it to the selected
      // colour once we are running.
      const initialSelection = currentSelection();
      const initialVariant = findVariant(initialSelection);
      if (initialVariant) {
        showFor(stage, String(initialVariant.id));
        showFor(thumbs, String(initialVariant.id));
        markUnavailable(initialSelection);
      }

      /* Thumbnails select which photo the stage shows. Only ever among the
         visible ones — a hidden thumbnail belongs to another colour. */
      const onThumb = (e) => {
        const btn = e.target.closest('button');
        if (!btn || btn.hidden) return;
        const visThumbs = galleryChildren(thumbs).filter((el) => !el.hidden);
        const visShots = galleryChildren(stage).filter((el) => !el.hidden);
        const i = visThumbs.indexOf(btn);
        if (i < 0) return;
        visThumbs.forEach((t, k) => t.classList.toggle('on', k === i));
        visShots.forEach((s, k) => s.classList.toggle('on', k === i));
      };
      if (thumbs) thumbs.addEventListener('click', onThumb);

      /* Clicking the main image advances to the next photo, as in the
         prototype. The cursor is set to pointer rather than zoom-in, because
         there is no lightbox and promising one is worse than not having it.
         Auglio's try-on button lives inside .pdp-stage too (see
         galleryChildren above) and has its own click behaviour — without
         this guard, clicking it would also advance the gallery underneath
         it, since this listener sits on the whole stage, not just the
         images. */
      const goTo = (step) => {
        const vis = galleryChildren(stage).filter((el) => !el.hidden);
        if (vis.length < 2) return;
        const cur = vis.findIndex((el) => el.classList.contains('on'));
        const next = (cur + step + vis.length) % vis.length;
        vis.forEach((s, k) => s.classList.toggle('on', k === next));
        if (thumbs) {
          const visThumbs = galleryChildren(thumbs).filter((el) => !el.hidden);
          visThumbs.forEach((t, k) => t.classList.toggle('on', k === next));
          visThumbs[next]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      };

      /* Swipe. A horizontal drag past the threshold steps the gallery; a
         mostly-vertical one is left to the page so scrolling still works.
         The click that follows a swipe is swallowed, otherwise it would step
         the gallery a second time. */
      let startX = null;
      let startY = 0;
      let swiped = false;
      const onDown = (e) => {
        if (e.target.closest('.auglio-tryon-btn')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        startX = e.clientX;
        startY = e.clientY;
        swiped = false;
      };
      const onUp = (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          swiped = true;
          goTo(dx < 0 ? 1 : -1);
        }
      };
      const onCancel = () => {
        startX = null;
      };

      const onStage = (e) => {
        if (e.target.closest('.auglio-tryon-btn')) return;
        if (swiped) {
          swiped = false;
          return;
        }
        goTo(1);
      };
      if (stage) {
        stage.addEventListener('click', onStage);
        stage.addEventListener('pointerdown', onDown);
        stage.addEventListener('pointerup', onUp);
        stage.addEventListener('pointercancel', onCancel);
      }

      return { swatches: allButtons, onSwatch, thumbs, onThumb, stage, onStage, onDown, onUp, onCancel };
    },

    unmount(root, s) {
      if (!s) return;
      s.swatches.forEach((b) => b.removeEventListener('click', s.onSwatch));
      s.thumbs?.removeEventListener('click', s.onThumb);
      s.stage?.removeEventListener('click', s.onStage);
      s.stage?.removeEventListener('pointerdown', s.onDown);
      s.stage?.removeEventListener('pointerup', s.onUp);
      s.stage?.removeEventListener('pointercancel', s.onCancel);
    },
  });
})();
